import { ItemActions } from './components/item-actions.tsx'
import { ItemContent } from './components/item-content.tsx'
import { ItemDescription } from './components/item-description.tsx'
import { ItemDetails } from './components/item-details.tsx'
import { ItemGroup } from './components/item-group.tsx'
import { ItemIndicator } from './components/item-indicator.tsx'
import { ItemMedia } from './components/item-media.tsx'
import { ItemRoot } from './components/item-root.tsx'
import { ItemSeparator } from './components/item-separator.tsx'
import { ItemTitle } from './components/item-title.tsx'

/**
 * Item is a composable list row: media + content (title/description) + actions.
 * Defaults to a `<div>`; pass `render={<Link .../>}` to make the whole row a link, and `interactive`
 * to give it the clickable affordance (cursor, hover fill, focus outline). Standalone rows are bordered by
 * default; `plain` removes their frame. Groups default to a framed list; `divided` keeps separators without the frame.
 *
 * **Styling hooks**:
 * - `data-variant` on `Item.Media`: `icon` draws a 32px `rounded-20` box with a brand-gradient
 *   background and a 16px glyph; `default` draws nothing.
 * - `data-slot="item"` / `data-slot="item-group"` / `data-slot="item-listitem"`: the targeting hooks callers and
 *   `Item.Group`'s `divided` variant rely on (`[&>[data-slot=item-listitem]>[data-slot=item]]:px-0`).
 * - `Item.Actions` must be a direct child of `Item.Root` so the row can keep actions outside its primary target.
 *
 * @summary List row compound (media / content / title / description / actions) + group + separator.
 */
export const Item = {
  Root: ItemRoot,
  Group: ItemGroup,
  Media: ItemMedia,
  Content: ItemContent,
  Title: ItemTitle,
  Description: ItemDescription,
  Details: ItemDetails,
  Actions: ItemActions,
  Indicator: ItemIndicator,
  Separator: ItemSeparator,
}

export namespace Item {
  export namespace Root {
    export type Props = ItemRoot.Props
  }
  export namespace Group {
    export type Props = ItemGroup.Props
  }
  export namespace Media {
    export type Props = ItemMedia.Props
  }
  export namespace Content {
    export type Props = ItemContent.Props
  }
  export namespace Title {
    export type Props = ItemTitle.Props
  }
  export namespace Description {
    export type Props = ItemDescription.Props
  }
  export namespace Details {
    export type Props = ItemDetails.Props
  }
  export namespace Actions {
    export type Props = ItemActions.Props
  }
  export namespace Indicator {
    export type Props = ItemIndicator.Props
  }
  export namespace Separator {
    export type Props = ItemSeparator.Props
  }
}

export { itemGroupVariants, itemTitleVariants, itemVariants } from './components/item.variants.ts'
